import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { requestDetailPath } from "@vv/types";
import { Button, showToast } from "@vv/ui";
import { useTeamsSelector } from "@vv/teams-frontend";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ValidationType } from "../../domain/request.types";
import { useRequestsDispatch } from "../../state/hooks";
import { createRequest, uploadAttachment } from "../../state/requests.thunks";
import { StepAttachmentsReview } from "./StepAttachmentsReview";
import { StepDetails } from "./StepDetails";
import { useWizardState } from "./useWizardState";

const STEPS = ["Request Details", "Attachments & Review"];

export function RequestWizardPage() {
  const dispatch = useRequestsDispatch();
  const navigate = useNavigate();
  const { form, update, isDetailsValid } = useWizardState();
  const teams = useTeamsSelector((state) => state.teams.teams);

  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const teamName = teams.find((team) => team.id === form.teamId)?.name ?? "";

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const created = await dispatch(
        createRequest({
          teamId: form.teamId,
          title: form.title.trim(),
          description: form.description.trim(),
          validationType: form.validationType as ValidationType,
        }),
      ).unwrap();
      showToast(`${created.displayId} submitted`, "success");

      // Files picked in step 2 are only local File objects until now - upload
      // them against the request id we just got back. A failed upload
      // shouldn't block navigation since the request itself was created
      // successfully; the user can retry from the detail page.
      const uploadFailures = (
        await Promise.all(
          form.attachments.map((file) =>
            dispatch(uploadAttachment({ requestId: created.id, file, category: "Evidence" }))
              .unwrap()
              .then(() => null)
              .catch(() => file.name),
          ),
        )
      ).filter((name): name is string => name !== null);
      if (uploadFailures.length > 0) {
        showToast(`Could not upload: ${uploadFailures.join(", ")}`, "error");
      }

      navigate(requestDetailPath(created.id));
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not submit request", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        New Validation Request
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 ? <StepDetails form={form} update={update} /> : null}
      {activeStep === 1 ? (
        <StepAttachmentsReview form={form} update={update} teamName={teamName} />
      ) : null}

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        {activeStep > 0 ? (
          <Button onClick={() => setActiveStep((step) => step - 1)}>Back</Button>
        ) : null}
        {activeStep === 0 ? (
          <Button variant="contained" disabled={!isDetailsValid} onClick={() => setActiveStep(1)}>
            Next
          </Button>
        ) : (
          <Button variant="contained" loading={submitting} onClick={handleSubmit}>
            Submit request
          </Button>
        )}
      </Stack>
    </Box>
  );
}
